import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import AuthTabs from './components/AuthTabs';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import TrustSignals from './components/TrustSignals';
import { useAuth } from '../../contexts/AuthContext';

const Authentication = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('next') || '/dashboard';
  const { user, loading } = useAuth();

  const [activeTab, setActiveTab] = useState('login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Redirect authenticated users away from the sign-in page immediately.
  // This also handles users navigating back to the authentication page after
  // completing a sign-in.
  useEffect(() => {
    if (loading || !user) return;
    navigate(redirectTo, { replace: true });
  }, [user, loading, navigate, redirectTo]);

  // While auth state is being resolved, show nothing to avoid flashing the
  // sign-in form before the redirect fires.
  if (loading || user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleAuthSuccess = () => {
    navigate(redirectTo, { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>Sign In - WebForms</title>
        <meta name="description" content="Sign in to your WebForms account to create, manage, and deploy custom forms with our powerful no-code form builder platform." />
      </Helmet>
      <div className="relative min-h-screen bg-[#f8fbfa] flex justify-center overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-[53%] bg-[#e6f3f0] lg:block" />
        <div className="pointer-events-none absolute -left-24 top-[42%] hidden h-80 w-80 rounded-full border-[40px] border-[#c7e6df] opacity-70 lg:block" />
        <div className="relative z-10 w-full max-w-6xl flex min-h-screen">
        <aside className="hidden lg:flex lg:w-[53%] relative overflow-hidden">
          <div className="relative z-10 flex w-full flex-col justify-center px-12 py-14 xl:px-16">
            <div className="max-w-md">
              <div className="mb-14 w-56">
                <Image src="/assets/images/web-forms-logo-1769801123836.png" alt="WebForms.biz logo" className="w-full h-auto object-contain" />
              </div>

              <div className="space-y-7">
                <div className="h-1 w-12 rounded-full bg-[#0e7572]" />
                <h1 className="text-[2.7rem] xl:text-5xl font-heading font-bold text-[#123c3a] leading-[1.08] tracking-tight">
                  Forms that feel<br />as good as they work.
                </h1>
                <p className="max-w-sm text-[1.05rem] leading-7 text-[#4c6966]">
                  A flexible workspace for teams who care about every response, every detail, and every conversion.
                </p>

                <div className="grid grid-cols-2 gap-x-5 gap-y-6 pt-5">
                {[
                { icon: 'Zap', label: 'Lightning Fast', description: 'Optimized performance' },
                { icon: 'Palette', label: 'Fully Customizable', description: 'Complete design control' },
                { icon: 'Code2', label: 'Easy Embedding', description: 'Deploy anywhere' },
                { icon: 'Shield', label: 'Secure by Default', description: 'Encrypted & access-controlled' }]?.
                map((feature, index) =>
                <div key={index} className="flex gap-3">
                    <div className="w-10 h-10 bg-[#d4ebe6] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={feature?.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#173f3d]">{feature?.label}</div>
                      <div className="text-xs text-[#66817e]">{feature?.description}</div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </aside>

        <main className="flex-1 flex items-center justify-center px-5 py-10 sm:px-8 lg:px-12 lg:py-14">
          <div className="w-full max-w-[27rem]">
            <div className="lg:hidden mb-9">
              <div className="mx-auto w-52 sm:w-56">
                <Image src="/assets/images/web-forms-logo-1769800970615.png" alt="WebForms.biz logo" className="w-full h-auto object-contain" />
              </div>
              <p className="mt-5 text-center text-sm text-[#597572]">
                Your workspace for considered, high-converting forms.
              </p>
            </div>

            <div className="bg-card border border-[#d9e8e4] rounded-2xl shadow-[0_18px_50px_rgba(14,117,114,0.10)] p-6 sm:p-8 transition-smooth">
              <div className="mb-7">
                <div className="mb-4 h-1 w-9 rounded-full bg-[#f97316]" />
                <h2 className="text-[1.7rem] font-heading font-bold text-[#163c39] mb-2 tracking-tight">
                  {activeTab === 'login' ? 'Welcome Back' : 'Get Started'}
                </h2>
                <p className="text-sm leading-6 text-[#617b78]">
                  {activeTab === 'login' ? 'Sign in to pick up where your best work left off.' : 'Start building forms your team will be proud to send.'
                  }
                </p>
              </div>

              <AuthTabs activeTab={activeTab} onTabChange={setActiveTab} />

              {activeTab === 'login' ?
              <LoginForm
                onForgotPassword={() => setShowForgotPassword(true)}
                onSuccess={handleAuthSuccess} /> :


              <RegisterForm
                onSuccess={handleAuthSuccess}
                onSwitchToLogin={() => setActiveTab('login')}
                redirectTo={redirectTo} />
              }

              <TrustSignals />
            </div>

            <p className="text-center text-xs leading-5 text-[#6a817e] mt-6 px-3">
              By continuing, you agree to WebForms's{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">
                Privacy Policy
              </a>
            </p>
          </div>
        </main>
        </div>
      </div>
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)} />

    </>);

};

export default Authentication;